import { computed, type ComputedRef } from 'vue';
import type { SearchResponse } from '@/api/search';
import { Users, Flag, Target, Shield, FileText, Grid3x3 } from 'lucide-vue-next';
import type { Component } from 'vue';

/**
 * Shared flattening for the global search backend response.
 *
 * The SearchResponse groups hits by artifact type. Both the TopBar
 * dropdown (GlobalSearchResults.vue) and the command palette
 * (CommandPalette.vue) need the same flat list for arrow-key
 * navigation and the same deep-link routing shape. Duplicating the
 * mapping once was tolerable; twice would rot the moment a new
 * artifact type lands. One source of truth per CLAUDE.md
 * §Architecture Rules (Smart Backend + SSOT).
 *
 * This composable is pure/derived — no side effects, no store writes.
 */

export interface FlatSearchResult {
  group: string;
  groupIcon: Component;
  label: string;
  sublabel?: string;
  severity?: string;
  id: string;
  route: { path: string; query?: Record<string, string> };
}

export function useFlatSearchResults(
  results: ComputedRef<SearchResponse | null> | { value: SearchResponse | null },
): ComputedRef<FlatSearchResult[]> {
  return computed<FlatSearchResult[]>(() => {
    const r = results.value?.results;
    if (!r) return [];
    const out: FlatSearchResult[] = [];

    for (const a of r.actors) {
      out.push({
        group: 'Actors',
        groupIcon: Users,
        label: a.name,
        sublabel: a.type,
        severity: a.threatLevel,
        id: `a-${a.id}`,
        route: { path: '/', query: { actor: String(a.id) } },
      });
    }
    for (const c of r.campaigns) {
      out.push({
        group: 'Campaigns',
        groupIcon: Flag,
        label: c.name,
        sublabel: c.status,
        severity: c.severity,
        id: `c-${c.id}`,
        route: { path: '/campaigns', query: { id: String(c.id) } },
      });
    }
    for (const i of r.indicators) {
      out.push({
        group: 'Indicators',
        groupIcon: Target,
        label: i.value,
        sublabel: i.type,
        severity: i.severity,
        id: `i-${i.id}`,
        route: { path: '/indicators', query: { id: String(i.id) } },
      });
    }
    for (const rl of r.rules) {
      out.push({
        group: 'Detection Rules',
        groupIcon: Shield,
        label: rl.name,
        severity: rl.severity,
        id: `r-${rl.id}`,
        route: { path: '/detection-rules', query: { rule: String(rl.id) } },
      });
    }
    for (const rep of r.reports) {
      out.push({
        group: 'Reports',
        groupIcon: FileText,
        label: rep.title,
        sublabel: rep.status,
        severity: rep.overallThreatLevel,
        id: `rep-${rep.id}`,
        route: { path: `/reports/${rep.id}` },
      });
    }
    for (const t of r.techniques) {
      out.push({
        group: 'MITRE',
        groupIcon: Grid3x3,
        label: `${t.id} — ${t.name}`,
        sublabel: t.tacticId,
        id: `t-${t.id}`,
        route: { path: '/mitre-matrix', query: { technique: t.id } },
      });
    }
    return out;
  });
}
